import {
  Excalidraw,
  LiveCollaborationTrigger,
  TTDDialogTrigger,
  CaptureUpdateAction,
  reconcileElements,
  useEditorInterface,
  ExcalidrawAPIProvider,
  useExcalidrawAPI,
} from "@excalidraw/excalidraw";
import { trackEvent } from "@excalidraw/excalidraw/analytics";
import {
  CommandPalette,
  DEFAULT_CATEGORIES,
} from "@excalidraw/excalidraw/components/CommandPalette/CommandPalette";
import { ErrorDialog } from "@excalidraw/excalidraw/components/ErrorDialog";
import { OverwriteConfirmDialog } from "@excalidraw/excalidraw/components/OverwriteConfirm/OverwriteConfirm";
import { openConfirmModal } from "@excalidraw/excalidraw/components/OverwriteConfirm/OverwriteConfirmState";
import Trans from "@excalidraw/excalidraw/components/Trans";
import {
  APP_NAME,
  EVENT,
  THEME,
  VERSION_TIMEOUT,
  debounce,
  getVersion,
  getFrame,
  isTestEnv,
  preventUnload,
  resolvablePromise,
  isRunningInIframe,
  isDevEnv,
} from "@excalidraw/common";
import polyfill from "@excalidraw/excalidraw/polyfill";
import { useCallback, useEffect, useRef, useState } from "react";
import { loadFromJSON, saveAsJSON } from "@excalidraw/excalidraw/data";
import { loadFromBlob } from "@excalidraw/excalidraw/data/blob";
import { t } from "@excalidraw/excalidraw/i18n";

import {
  usersIcon,
  SunIcon,
  MoonIcon,
  DeviceDesktopIcon,
} from "@excalidraw/excalidraw/components/icons";
import { isElementLink } from "@excalidraw/element";
import {
  bumpElementVersions,
  restoreAppState,
  restoreElements,
} from "@excalidraw/excalidraw/data/restore";
import { newElementWith } from "@excalidraw/element";
import { isInitializedImageElement } from "@excalidraw/element";
import clsx from "clsx";
import {
  parseLibraryTokensFromUrl,
  useHandleLibrary,
} from "@excalidraw/excalidraw/data/library";

import type { RemoteExcalidrawElement } from "@excalidraw/excalidraw/data/reconcile";
import type { RestoredDataState } from "@excalidraw/excalidraw/data/restore";
import type {
  FileId,
  NonDeletedExcalidrawElement,
  OrderedExcalidrawElement,
} from "@excalidraw/element/types";
import type {
  AppState,
  ExcalidrawImperativeAPI,
  BinaryFiles,
  ExcalidrawInitialDataState,
  UIAppState,
  ExcalidrawProps,
} from "@excalidraw/excalidraw/types";
import type { ResolutionType } from "@excalidraw/common/utility-types";
import type { ResolvablePromise } from "@excalidraw/common/utils";

import CustomStats from "./CustomStats";
import { LanguageList } from "./app-language/LanguageList";
import {
  Provider,
  useAtom,
  useAtomValue,
  useAtomWithInitialValue,
  appJotaiStore,
} from "./app-jotai";
import {
  FIREBASE_STORAGE_PREFIXES,
  STORAGE_KEYS,
  SYNC_BROWSER_TABS_TIMEOUT,
} from "./app_constants";
import Collab, {
  collabAPIAtom,
  isCollaboratingAtom,
  isOfflineAtom,
} from "./collab/Collab";
import { AppFooter } from "./components/AppFooter";
import { AppMainMenu } from "./components/AppMainMenu";
import { AppWelcomeScreen } from "./components/AppWelcomeScreen";
import { TopErrorBoundary } from "./components/TopErrorBoundary";

import {
  getCollaborationLinkData,
  importFromBackend,
  isCollaborationLink,
} from "./data";

import { updateStaleImageStatuses } from "./data/FileManager";
import { FileStatusStore } from "./data/fileStatusStore";
import {
  importFromLocalStorage,
  importUsernameFromLocalStorage,
} from "./data/localStorage";

import { loadFilesFromFirebase } from "./data/firebase";
import {
  LibraryIndexedDBAdapter,
  LibraryLocalStorageMigrationAdapter,
  LocalData,
  localStorageQuotaExceededAtom,
} from "./data/LocalData";
import { isBrowserStorageStateNewer } from "./data/tabSync";
import { shareDialogStateAtom } from "./share/ShareDialog";
import CollabError, { collabErrorIndicatorAtom } from "./collab/CollabError";
import { useHandleAppTheme } from "./useHandleAppTheme";
import { getPreferredLanguage } from "./app-language/language-detector";
import { useAppLangCode } from "./app-language/language-state";
import DebugCanvas, {
  debugRenderer,
  isVisualDebuggerEnabled,
  loadSavedDebugState,
} from "./components/DebugCanvas";
import { AIComponents } from "./components/AI";

import "./index.scss";

import { AppSidebar } from "./components/AppSidebar";

import type { CollabAPI } from "./collab/Collab";

polyfill();

window.EXCALIDRAW_THROTTLE_RENDER = true;

declare global {
  interface BeforeInstallPromptEventChoiceResult {
    outcome: "accepted" | "dismissed";
  }

  interface BeforeInstallPromptEvent extends Event {
    prompt(): Promise<void>;
    userChoice: Promise<BeforeInstallPromptEventChoiceResult>;
  }

  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent;
  }
}

let pwaEvent: BeforeInstallPromptEvent | null = null;

// Adding a listener outside of the component as it may (?) need to be
// subscribed early to catch the event.
//
// Also note that it will fire only if certain heuristics are met (user has
// used the app for some time, etc.)
window.addEventListener(
  "beforeinstallprompt",
  (event: BeforeInstallPromptEvent) => {
    // prevent Chrome <= 67 from automatically showing the prompt
    event.preventDefault();
    // cache for later use
    pwaEvent = event;
  },
);

let isSelfEmbedding = false;

if (window.self !== window.top) {
  try {
    const parentUrl = new URL(document.referrer);
    const currentUrl = new URL(window.location.href);
    if (parentUrl.origin === currentUrl.origin) {
      isSelfEmbedding = true;
    }
  } catch (error) {
    // ignore
  }
}

const shareableLinkConfirmDialog = {
  title: t("overwriteConfirm.modal.shareableLink.title"),
  description: (
    <Trans
      i18nKey="overwriteConfirm.modal.shareableLink.description"
      bold={(text) => <strong>{text}</strong>}
      br={() => <br />}
    />
  ),
  actionLabel: t("overwriteConfirm.modal.shareableLink.button"),
  color: "danger",
} as const;

const initializeScene = async (opts: {
  collabAPI: CollabAPI | null;
  excalidrawAPI: ExcalidrawImperativeAPI;
}): Promise<
  { scene: ExcalidrawInitialDataState | null } & (
    | { isExternalScene: true; id: string; key: string }
    | { isExternalScene: false; id?: null; key?: null }
  )
> => {
  const searchParams = new URLSearchParams(window.location.search);
  const id = searchParams.get("id");
  const jsonBackendMatch = window.location.hash.match(
    /^#json=([a-zA-Z0-9_-]+),([a-zA-Z0-9_-]+)$/,
  );
  const externalUrlMatch = window.location.hash.match(/^#url=(.*)$/);

  const localDataState = importFromLocalStorage();

  let scene: Omit<
    RestoredDataState,
    // we're not storing files in the scene database/localStorage, and instead
    // fetch them async from a different store
    "files"
  > & {
    scrollToContent?: boolean;
  } = {
    elements: restoreElements(localDataState?.elements, null, {
      repairBindings: true,
      deleteInvisibleElements: true,
    }),
    appState: restoreAppState(localDataState?.appState, null),
  };

  let roomLinkData = getCollaborationLinkData(window.location.href);
  const isExternalScene = !!(id || jsonBackendMatch || roomLinkData);
  if (isExternalScene) {
    if (
      // don't prompt if scene is empty
      !scene.elements.length ||
      // don't prompt for collab scenes because we don't override local storage
      roomLinkData ||
      // otherwise, prompt whether user wants to override current scene
      (await openConfirmModal(shareableLinkConfirmDialog))
    ) {
      if (jsonBackendMatch) {
        const imported = await importFromBackend(
          jsonBackendMatch[1],
          jsonBackendMatch[2],
        );

        scene = {
          elements: bumpElementVersions(
            restoreElements(imported.elements, null, {
              repairBindings: true,
              deleteInvisibleElements: true,
            }),
            localDataState?.elements,
          ),
          appState: restoreAppState(
            imported.appState,
            // local appState when importing from backend to ensure we restore
            // localStorage user settings which we do not persist on server.
            localDataState?.appState,
          ),
        };
      }
      scene.scrollToContent = true;
      if (!roomLinkData) {
        window.history.replaceState({}, APP_NAME, window.location.origin);
      }
    } else {
      // https://github.com/excalidraw/excalidraw/issues/1919
      if (document.hidden) {
        return new Promise((resolve, reject) => {
          window.addEventListener(
            "focus",
            () => initializeScene(opts).then(resolve).catch(reject),
            {
              once: true,
            },
          );
        });
      }

      roomLinkData = null;
      window.history.replaceState({}, APP_NAME, window.location.origin);
    }
  } else if (externalUrlMatch) {
    window.history.replaceState({}, APP_NAME, window.location.origin);

    const url = externalUrlMatch[1];
    try {
      const request = await fetch(window.decodeURIComponent(url));
      const data = await loadFromBlob(await request.blob(), null, null);
      if (
        !scene.elements.length ||
        (await openConfirmModal(shareableLinkConfirmDialog))
      ) {
        return { scene: data, isExternalScene };
      }
    } catch (error: any) {
      return {
        scene: {
          appState: {
            errorMessage: t("alerts.invalidSceneUrl"),
          },
        },
        isExternalScene,
      };
    }
  }

  if (roomLinkData && opts.collabAPI) {
    const { excalidrawAPI } = opts;

    const scene = await opts.collabAPI.startCollaboration(roomLinkData);

    return {
      // when collaborating, the state may have already been updated at this
      // point (we may have received updates from other clients), so reconcile
      // elements and appState with existing state
      scene: {
        ...scene,
        appState: {
          ...restoreAppState(
            {
              ...scene?.appState,
              theme: localDataState?.appState?.theme || scene?.appState?.theme,
            },
            excalidrawAPI.getAppState(),
          ),
          // necessary if we're invoking from a hashchange handler which doesn't
          // go through App.initializeScene() that resets this flag
          isLoading: false,
        },
        elements: reconcileElements(
          scene?.elements || [],
          excalidrawAPI.getSceneElementsIncludingDeleted() as RemoteExcalidrawElement[],
          excalidrawAPI.getAppState(),
        ),
      },
      isExternalScene: true,
      id: roomLinkData.roomId,
      key: roomLinkData.roomKey,
    };
  } else if (scene) {
    return isExternalScene && jsonBackendMatch
      ? {
          scene,
          isExternalScene,
          id: jsonBackendMatch[1],
          key: jsonBackendMatch[2],
        }
      : { scene, isExternalScene: false };
  }
  return { scene: null, isExternalScene: false };
};

const ExcalidrawWrapper = () => {
  const excalidrawAPI = useExcalidrawAPI();

  const [errorMessage, setErrorMessage] = useState("");
  const isCollabFeatureEnabled =
    (import.meta.env.VITE_APP_ENABLE_COLLAB || "") === "true";
  const isAiFeatureEnabled = (import.meta.env.VITE_APP_ENABLE_AI || "") === "true";
  const isAdvancedToolsEnabled =
    (import.meta.env.VITE_APP_ENABLE_ADVANCED_TOOLS || "") === "true";
  const isCommandPaletteEnabled =
    (import.meta.env.VITE_APP_ENABLE_COMMAND_PALETTE || "") === "true";
  const isSearchEnabled =
    (import.meta.env.VITE_APP_ENABLE_SEARCH || "") === "true";
  const isCollabDisabled = !isCollabFeatureEnabled || isRunningInIframe();

  const { editorTheme, appTheme, setAppTheme } = useHandleAppTheme();

  const [langCode, setLangCode] = useAppLangCode();

  const editorInterface = useEditorInterface();

  useEffect(() => {
    document.title = langCode.startsWith("zh")
      ? "ZHIRAI 自由画板"
      : "ZHIRAI Board";
  }, [langCode]);

  // initial state
  // ---------------------------------------------------------------------------

  const initialStatePromiseRef = useRef<{
    promise: ResolvablePromise<ExcalidrawInitialDataState | null>;
  }>({ promise: null! });
  if (!initialStatePromiseRef.current.promise) {
    initialStatePromiseRef.current.promise =
      resolvablePromise<ExcalidrawInitialDataState | null>();
  }

  const debugCanvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    trackEvent("load", "frame", getFrame());
    // Delayed so that the app has a time to load the latest SW
    setTimeout(() => {
      trackEvent("load", "version", getVersion());
    }, VERSION_TIMEOUT);
  }, []);

  const [, setShareDialogState] = useAtom(shareDialogStateAtom);
  const [collabAPI] = useAtom(collabAPIAtom);
  const effectiveCollabAPI = isCollabDisabled ? null : collabAPI;
  const [isCollaborating] = useAtomWithInitialValue(isCollaboratingAtom, () => {
    return !isCollabDisabled && isCollaborationLink(window.location.href);
  });
  const collabError = useAtomValue(collabErrorIndicatorAtom);

  useHandleLibrary({
    excalidrawAPI,
    adapter: LibraryIndexedDBAdapter,
    // TODO maybe remove this in several months (shipped: 24-03-11)
    migrationAdapter: LibraryLocalStorageMigrationAdapter,
  });

  const [, forceRefresh] = useState(false);
  const [isPreferencesOpen, setIsPreferencesOpen] = useState(false);
  const preferencesRef = useRef<HTMLDivElement>(null);

  const updateEditorAppState = useCallback(
    <K extends keyof AppState>(patch: (appState: AppState) => Pick<AppState, K>) => {
      if (!excalidrawAPI) {
        return;
      }
      const appState = excalidrawAPI.getAppState();
      excalidrawAPI.updateScene({
        appState: patch(appState),
      });
      forceRefresh((prev) => !prev);
    },
    [excalidrawAPI],
  );

  useEffect(() => {
    if (!isPreferencesOpen) {
      return;
    }

    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (target && preferencesRef.current?.contains(target)) {
        return;
      }
      setIsPreferencesOpen(false);
    };

    document.addEventListener("pointerdown", onPointerDown, true);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
    };
  }, [isPreferencesOpen]);

  useEffect(() => {
    if (isDevEnv()) {
      const debugState = loadSavedDebugState();

      if (debugState.enabled && !window.visualDebug) {
        window.visualDebug = {
          data: [],
        };
      } else {
        delete window.visualDebug;
      }
      forceRefresh((prev) => !prev);
    }
  }, [excalidrawAPI]);

  // ---------------------------------------------------------------------------
  // Hoisted loadImages
  // ---------------------------------------------------------------------------
  const loadImages = useCallback(
    (data: ResolutionType<typeof initializeScene>, isInitialLoad = false) => {
      if (!data.scene || !excalidrawAPI) {
        return;
      }

      if (effectiveCollabAPI?.isCollaborating()) {
        if (data.scene.elements) {
          effectiveCollabAPI
            .fetchImageFilesFromFirebase({
              elements: data.scene.elements,
              forceFetchFiles: true,
            })
            .then(({ loadedFiles, erroredFiles }) => {
              excalidrawAPI.addFiles(loadedFiles);
              updateStaleImageStatuses({
                excalidrawAPI,
                erroredFiles,
                elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
              });
            });
        }
      } else {
        const fileIds =
          data.scene.elements?.reduce((acc, element) => {
            if (isInitializedImageElement(element)) {
              return acc.concat(element.fileId);
            }
            return acc;
          }, [] as FileId[]) || [];

        if (data.isExternalScene) {
          if (fileIds.length) {
            // Direct Firebase call (not through FileManager), so track manually
            FileStatusStore.updateStatuses(
              fileIds.map((id) => [id, "loading"]),
            );
          }
          loadFilesFromFirebase(
            `${FIREBASE_STORAGE_PREFIXES.shareLinkFiles}/${data.id}`,
            data.key,
            fileIds,
          ).then(({ loadedFiles, erroredFiles }) => {
            excalidrawAPI.addFiles(loadedFiles);
            updateStaleImageStatuses({
              excalidrawAPI,
              erroredFiles,
              elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
            });
            FileStatusStore.updateStatuses([
              ...loadedFiles.map((f) => [f.id, "loaded"] as [FileId, "loaded"]),
              ...[...erroredFiles.keys()].map(
                (id) => [id, "error"] as [FileId, "error"],
              ),
            ]);
          });
        } else if (isInitialLoad) {
          if (fileIds.length) {
            LocalData.fileStorage
              .getFiles(fileIds)
              .then(async ({ loadedFiles, erroredFiles }) => {
                if (loadedFiles.length) {
                  excalidrawAPI.addFiles(loadedFiles);
                }
                updateStaleImageStatuses({
                  excalidrawAPI,
                  erroredFiles,
                  elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
                });
              });
          }
          // on fresh load, clear unused files from IDB (from previous
          // session)
          LocalData.fileStorage.clearObsoleteFiles({
            currentFileIds: fileIds,
          });
        }
      }
    },
    [effectiveCollabAPI, excalidrawAPI],
  );

  useEffect(() => {
    if (!excalidrawAPI || (!isCollabDisabled && !effectiveCollabAPI)) {
      return;
    }

    initializeScene({ collabAPI: effectiveCollabAPI, excalidrawAPI }).then(
      async (data) => {
      loadImages(data, /* isInitialLoad */ true);
      initialStatePromiseRef.current.promise.resolve(data.scene);
      },
    );

    const onHashChange = async (event: HashChangeEvent) => {
      event.preventDefault();
      const libraryUrlTokens = parseLibraryTokensFromUrl();
      if (!libraryUrlTokens) {
        if (
          effectiveCollabAPI?.isCollaborating() &&
          !isCollaborationLink(window.location.href)
        ) {
          effectiveCollabAPI.stopCollaboration(false);
        }
        excalidrawAPI.updateScene({ appState: { isLoading: true } });

        initializeScene({ collabAPI: effectiveCollabAPI, excalidrawAPI }).then(
          (data) => {
            loadImages(data);
            if (data.scene) {
              excalidrawAPI.updateScene({
                elements: restoreElements(data.scene.elements, null, {
                  repairBindings: true,
                }),
                appState: restoreAppState(data.scene.appState, null),
                captureUpdate: CaptureUpdateAction.IMMEDIATELY,
              });
            }
          },
        );
      }
    };

    const syncData = debounce(() => {
      if (isTestEnv()) {
        return;
      }
      if (
        !document.hidden &&
        ((effectiveCollabAPI && !effectiveCollabAPI.isCollaborating()) ||
          isCollabDisabled)
      ) {
        // don't sync if local state is newer or identical to browser state
        if (isBrowserStorageStateNewer(STORAGE_KEYS.VERSION_DATA_STATE)) {
          const localDataState = importFromLocalStorage();
          const username = importUsernameFromLocalStorage();
          setLangCode(getPreferredLanguage());
          excalidrawAPI.updateScene({
            ...localDataState,
            captureUpdate: CaptureUpdateAction.NEVER,
          });
          LibraryIndexedDBAdapter.load().then((data) => {
            if (data) {
              excalidrawAPI.updateLibrary({
                libraryItems: data.libraryItems,
              });
            }
          });
          effectiveCollabAPI?.setUsername(username || "");
        }

        if (isBrowserStorageStateNewer(STORAGE_KEYS.VERSION_FILES)) {
          const elements = excalidrawAPI.getSceneElementsIncludingDeleted();
          const currFiles = excalidrawAPI.getFiles();
          const fileIds =
            elements?.reduce((acc, element) => {
              if (
                isInitializedImageElement(element) &&
                // only load and update images that aren't already loaded
                !currFiles[element.fileId]
              ) {
                return acc.concat(element.fileId);
              }
              return acc;
            }, [] as FileId[]) || [];
          if (fileIds.length) {
            LocalData.fileStorage
              .getFiles(fileIds)
              .then(({ loadedFiles, erroredFiles }) => {
                if (loadedFiles.length) {
                  excalidrawAPI.addFiles(loadedFiles);
                }
                updateStaleImageStatuses({
                  excalidrawAPI,
                  erroredFiles,
                  elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
                });
              });
          }
        }
      }
    }, SYNC_BROWSER_TABS_TIMEOUT);

    const onUnload = () => {
      LocalData.flushSave();
    };

    const visibilityChange = (event: FocusEvent | Event) => {
      if (event.type === EVENT.BLUR || document.hidden) {
        LocalData.flushSave();
      }
      if (
        event.type === EVENT.VISIBILITY_CHANGE ||
        event.type === EVENT.FOCUS
      ) {
        syncData();
      }
    };

    window.addEventListener(EVENT.HASHCHANGE, onHashChange, false);
    window.addEventListener(EVENT.UNLOAD, onUnload, false);
    window.addEventListener(EVENT.BLUR, visibilityChange, false);
    document.addEventListener(EVENT.VISIBILITY_CHANGE, visibilityChange, false);
    window.addEventListener(EVENT.FOCUS, visibilityChange, false);
    return () => {
      window.removeEventListener(EVENT.HASHCHANGE, onHashChange, false);
      window.removeEventListener(EVENT.UNLOAD, onUnload, false);
      window.removeEventListener(EVENT.BLUR, visibilityChange, false);
      window.removeEventListener(EVENT.FOCUS, visibilityChange, false);
      document.removeEventListener(
        EVENT.VISIBILITY_CHANGE,
        visibilityChange,
        false,
      );
    };
  }, [
    isCollabDisabled,
    effectiveCollabAPI,
    excalidrawAPI,
    setLangCode,
    loadImages,
  ]);

  useEffect(() => {
    const unloadHandler = (event: BeforeUnloadEvent) => {
      LocalData.flushSave();

      if (
        excalidrawAPI &&
        LocalData.fileStorage.shouldPreventUnload(
          excalidrawAPI.getSceneElements(),
        )
      ) {
        if (import.meta.env.VITE_APP_DISABLE_PREVENT_UNLOAD !== "true") {
          preventUnload(event);
        } else {
          console.warn(
            "preventing unload disabled (VITE_APP_DISABLE_PREVENT_UNLOAD)",
          );
        }
      }
    };
    window.addEventListener(EVENT.BEFORE_UNLOAD, unloadHandler);
    return () => {
      window.removeEventListener(EVENT.BEFORE_UNLOAD, unloadHandler);
    };
  }, [excalidrawAPI]);

  const onChange = (
    elements: readonly OrderedExcalidrawElement[],
    appState: AppState,
    files: BinaryFiles,
  ) => {
    if (effectiveCollabAPI?.isCollaborating()) {
      effectiveCollabAPI.syncElements(elements);
    }

    // this check is redundant, but since this is a hot path, it's best
    // not to evaludate the nested expression every time
    if (!LocalData.isSavePaused()) {
      LocalData.save(elements, appState, files, () => {
        if (excalidrawAPI) {
          let didChange = false;

          const elements = excalidrawAPI
            .getSceneElementsIncludingDeleted()
            .map((element) => {
              if (
                LocalData.fileStorage.shouldUpdateImageElementStatus(element)
              ) {
                const newElement = newElementWith(element, { status: "saved" });
                if (newElement !== element) {
                  didChange = true;
                }
                return newElement;
              }
              return element;
            });

          if (didChange) {
            excalidrawAPI.updateScene({
              elements,
              captureUpdate: CaptureUpdateAction.NEVER,
            });
          }
        }
      });
    }

    // Render the debug scene if the debug canvas is available
    if (debugCanvasRef.current && excalidrawAPI) {
      debugRenderer(
        debugCanvasRef.current,
        appState,
        elements,
        window.devicePixelRatio,
      );
    }
  };

  const renderCustomStats = (
    elements: readonly NonDeletedExcalidrawElement[],
    appState: UIAppState,
  ) => {
    return (
      <CustomStats
        setToast={(message) => excalidrawAPI!.setToast({ message })}
        appState={appState}
        elements={elements}
      />
    );
  };

  const isOffline = useAtomValue(isOfflineAtom);

  const localStorageQuotaExceeded = useAtomValue(localStorageQuotaExceededAtom);

  const onCollabDialogOpen = useCallback(
    () => setShareDialogState({ isOpen: true, type: "collaborationOnly" }),
    [setShareDialogState],
  );

  // ---------------------------------------------------------------------------
  // onExport — intercepts file save to wait for pending image loads
  // ---------------------------------------------------------------------------
  const onExport: Required<ExcalidrawProps>["onExport"] = useCallback(
    async function* () {
      let snapshot = FileStatusStore.getSnapshot();
      const { pending, total } = FileStatusStore.getPendingCount(
        snapshot.value,
      );
      if (pending === 0) {
        return;
      }

      // Yield initial progress
      yield {
        type: "progress",
        progress: (total - pending) / total,
        message: `Loading images (${total - pending}/${total})...`,
      };

      // Wait for all pending images to finish
      while (true) {
        snapshot = await FileStatusStore.pull(snapshot.version);
        const { pending: nowPending, total: nowTotal } =
          FileStatusStore.getPendingCount(snapshot.value);

        yield {
          type: "progress",
          progress: (nowTotal - nowPending) / nowTotal,
          message: `Loading images (${nowTotal - nowPending}/${nowTotal})...`,
        };

        if (nowPending === 0) {
          await new Promise((r) => setTimeout(r, 500));
          yield {
            type: "progress",
            message: `Preparing export...`,
          };
          return;
        }
      }
    },
    [],
  );

  // const onExport = () => {
  //   return new Promise((r) => setTimeout(r, 2500));
  //   // console.log("onExport");
  // };

  // browsers generally prevent infinite self-embedding, there are
  // cases where it still happens, and while we disallow self-embedding
  // by not whitelisting our own origin, this serves as an additional guard
  if (isSelfEmbedding) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          height: "100%",
        }}
      >
        <h1>I'm not a pretzel!</h1>
      </div>
    );
  }

  return (
    <div
      style={{ height: "100%" }}
      className={clsx("excalidraw-app", {
        "is-collaborating": isCollaborating,
      })}
    >
      <Excalidraw
        onChange={onChange}
        onExport={onExport}
        initialData={initialStatePromiseRef.current.promise}
        isCollaborating={isCollaborating}
          onPointerUpdate={effectiveCollabAPI?.onPointerUpdate}
        aiEnabled={isAiFeatureEnabled}
        UIOptions={{
          canvasActions: {
            toggleTheme: true,
            export: false,
            saveAsImage: false,
          },
          tools: {
            frame: isAdvancedToolsEnabled,
            embeddable: isAdvancedToolsEnabled,
            laser: isAdvancedToolsEnabled,
            lasso: isAdvancedToolsEnabled,
            magicframe: isAiFeatureEnabled,
          },
        }}
        langCode={langCode}
        renderCustomStats={renderCustomStats}
        detectScroll={false}
        handleKeyboardGlobally={true}
        autoFocus={true}
        theme={editorTheme}
        renderTopLeftUI={(isMobile) => {
          if (isMobile) {
            return null;
          }

          const openMainMenuAndClick = (opts: {
            testId?: string;
            textPrefix: string;
          }) => {
            document
              .querySelector<HTMLButtonElement>(".main-menu-trigger")
              ?.click();
            let tries = 0;
            const tryClick = () => {
              const menu =
                document.querySelector<HTMLElement>('[role="menu"]') || null;
              const item =
                (opts.testId
                  ? menu?.querySelector<HTMLElement>(
                      `[data-testid="${opts.testId}"]`,
                    )
                  : null) ||
                Array.from(
                  menu?.querySelectorAll<HTMLElement>('[role="menuitem"]') || [],
                ).find((el) =>
                  (el.textContent || "").trim().startsWith(opts.textPrefix),
                );
              if (item) {
                item.click();
                return;
              }
              if (tries++ < 24) {
                window.requestAnimationFrame(tryClick);
              }
            };
            window.requestAnimationFrame(tryClick);
          };

          const editorAppState = excalidrawAPI?.getAppState() || null;

          return (
            <div className="zhirai-top-bar" aria-label="ZHIRAI">
              <a
                className="zhirai-top-brand"
                href="https://zhinian-ai.com"
                aria-label="返回 zhinian-ai.com"
              >
                <img
                  src="/zhirai-logo.png"
                  alt="ZHIRAI"
                  className="zhirai-top-brand__logo"
                />
                <div className="zhirai-top-brand__text">
                  <div className="zhirai-top-brand__name">ZHIRAI</div>
                  <div className="zhirai-top-brand__sub">自由画板</div>
                </div>
              </a>
              <nav className="zhirai-top-nav" aria-label="导航">
                <button
                  type="button"
                  className="zhirai-top-nav__btn"
                  onClick={async () => {
                    if (!excalidrawAPI) return;
                    try {
                      const imported = await loadFromJSON(
                        excalidrawAPI.getAppState(),
                        excalidrawAPI.getSceneElements(),
                      );

                      if (imported.files) {
                        excalidrawAPI.addFiles(Object.values(imported.files));
                      }

                      excalidrawAPI.updateScene({
                        elements: restoreElements(imported.elements, null, {
                          repairBindings: true,
                          deleteInvisibleElements: true,
                        }),
                        appState: restoreAppState(
                          imported.appState,
                          excalidrawAPI.getAppState(),
                        ),
                        captureUpdate: CaptureUpdateAction.IMMEDIATELY,
                      });

                      forceRefresh((prev) => !prev);
                    } catch (error: any) {
                      if (
                        error?.name === "AbortError" ||
                        String(error?.message || "").toLowerCase().includes("aborted")
                      ) {
                        return;
                      }
                      setErrorMessage(error?.message || "");
                    }
                  }}
                >
                  打开
                </button>
                <button
                  type="button"
                  className="zhirai-top-nav__btn"
                  onClick={async () => {
                    if (!excalidrawAPI) return;
                    try {
                      const { fileHandle } = await saveAsJSON({
                        data: {
                          elements: excalidrawAPI.getSceneElements(),
                          appState: excalidrawAPI.getAppState(),
                          files: excalidrawAPI.getFiles(),
                        },
                        filename: excalidrawAPI.getName() || "ZHIRAI",
                        fileHandle: excalidrawAPI.getAppState().fileHandle,
                      });
                      updateEditorAppState(() => ({ fileHandle }));
                    } catch (error: any) {
                      if (
                        error?.name === "AbortError" ||
                        String(error?.message || "").toLowerCase().includes("aborted")
                      ) {
                        return;
                      }
                      setErrorMessage(error?.message || "");
                    }
                  }}
                >
                  保存
                </button>
                <button
                  type="button"
                  className="zhirai-top-nav__btn"
                  onClick={() => {
                    excalidrawAPI?.resetScene();
                  }}
                >
                  重置
                </button>
                {isCommandPaletteEnabled && (
                  <button
                    type="button"
                    className="zhirai-top-nav__btn"
                    onClick={() => {
                      openMainMenuAndClick({
                        testId: "command-palette-button",
                        textPrefix: "命令",
                      });
                    }}
                  >
                    命令
                  </button>
                )}
                {isSearchEnabled && (
                  <button
                    type="button"
                    className="zhirai-top-nav__btn"
                    onClick={() => {
                      openMainMenuAndClick({
                        testId: "search-menu-button",
                        textPrefix: "在画布上查找",
                      });
                    }}
                  >
                    查找
                  </button>
                )}
                <div className="zhirai-pref" ref={preferencesRef}>
                  <button
                    type="button"
                    className="zhirai-top-nav__btn"
                    aria-expanded={isPreferencesOpen}
                    onClick={() => setIsPreferencesOpen((prev) => !prev)}
                  >
                    首选
                  </button>
                  {isPreferencesOpen && (
                    <div className="zhirai-pref-popover" role="dialog">
                      <div className="zhirai-pref-popover__title">首选项</div>

                      <div className="zhirai-pref-popover__section">
                        <div className="zhirai-pref-popover__section-title">
                          {t("labels.boxSelectionMode")}
                        </div>
                        <div className="zhirai-pref-popover__seg">
                          <button
                            type="button"
                            className={clsx("zhirai-pref-popover__seg-btn", {
                              "is-active":
                                (editorAppState?.boxSelectionMode || "contain") ===
                                "contain",
                            })}
                            onClick={() =>
                              updateEditorAppState(() => ({
                                boxSelectionMode: "contain",
                              }))
                            }
                          >
                            包含
                          </button>
                          <button
                            type="button"
                            className={clsx("zhirai-pref-popover__seg-btn", {
                              "is-active":
                                (editorAppState?.boxSelectionMode || "contain") ===
                                "overlap",
                            })}
                            onClick={() =>
                              updateEditorAppState(() => ({
                                boxSelectionMode: "overlap",
                              }))
                            }
                          >
                            接触
                          </button>
                        </div>
                      </div>

                      <label className="zhirai-pref-popover__check">
                        <input
                          type="checkbox"
                          checked={!!editorAppState?.activeTool?.locked}
                          onChange={() =>
                            updateEditorAppState((appState) => ({
                              activeTool: {
                                ...appState.activeTool,
                                locked: !appState.activeTool.locked,
                              },
                            }))
                          }
                        />
                        <span>{t("labels.preferences_toolLock")}</span>
                      </label>
                      <label className="zhirai-pref-popover__check">
                        <input
                          type="checkbox"
                          checked={!!editorAppState?.objectsSnapModeEnabled}
                          onChange={() =>
                            updateEditorAppState((appState) => ({
                              objectsSnapModeEnabled:
                                !appState.objectsSnapModeEnabled,
                            }))
                          }
                        />
                        <span>{t("buttons.objectsSnapMode")}</span>
                      </label>
                      <label className="zhirai-pref-popover__check">
                        <input
                          type="checkbox"
                          checked={!!editorAppState?.gridModeEnabled}
                          onChange={() =>
                            updateEditorAppState((appState) => ({
                              gridModeEnabled: !appState.gridModeEnabled,
                            }))
                          }
                        />
                        <span>{t("labels.toggleGrid")}</span>
                      </label>
                      <label className="zhirai-pref-popover__check">
                        <input
                          type="checkbox"
                          checked={!!editorAppState?.zenModeEnabled}
                          onChange={() =>
                            updateEditorAppState((appState) => ({
                              zenModeEnabled: !appState.zenModeEnabled,
                            }))
                          }
                        />
                        <span>{t("buttons.zenMode")}</span>
                      </label>
                      <label className="zhirai-pref-popover__check">
                        <input
                          type="checkbox"
                          checked={!!editorAppState?.viewModeEnabled}
                          onChange={() =>
                            updateEditorAppState((appState) => ({
                              viewModeEnabled: !appState.viewModeEnabled,
                            }))
                          }
                        />
                        <span>{t("labels.viewMode")}</span>
                      </label>
                      <label className="zhirai-pref-popover__check">
                        <input
                          type="checkbox"
                          checked={!!editorAppState?.stats?.open}
                          onChange={() =>
                            updateEditorAppState((appState) => ({
                              stats: {
                                ...appState.stats,
                                open: !appState.stats.open,
                              },
                            }))
                          }
                        />
                        <span>{t("stats.fullTitle")}</span>
                      </label>
                      <label className="zhirai-pref-popover__check">
                        <input
                          type="checkbox"
                          checked={editorAppState?.bindingPreference === "enabled"}
                          onChange={() =>
                            updateEditorAppState((appState) => ({
                              bindingPreference:
                                appState.bindingPreference === "enabled"
                                  ? "disabled"
                                  : "enabled",
                            }))
                          }
                        />
                        <span>{t("labels.arrowBinding")}</span>
                      </label>
                      <label className="zhirai-pref-popover__check">
                        <input
                          type="checkbox"
                          checked={!!editorAppState?.isMidpointSnappingEnabled}
                          onChange={() =>
                            updateEditorAppState((appState) => ({
                              isMidpointSnappingEnabled:
                                !appState.isMidpointSnappingEnabled,
                            }))
                          }
                        />
                        <span>{t("labels.midpointSnapping")}</span>
                      </label>
                    </div>
                  )}
                </div>
              </nav>
            </div>
          );
        }}
        renderTopRightUI={(isMobile) => {
          if (isMobile) {
            return null;
          }

          const themeIsLight = appTheme === THEME.LIGHT;
          const themeIsDark = appTheme === THEME.DARK;
          const themeIsSystem = appTheme === "system";

          return (
            <div className="excalidraw-ui-top-right zhirai-top-right">
              <div className="zhirai-theme-toggle" role="group" aria-label="主题">
                <button
                  type="button"
                  className={clsx("zhirai-theme-toggle__btn", {
                    "is-active": themeIsLight,
                  })}
                  aria-pressed={themeIsLight}
                  aria-label="浅色主题"
                  title="浅色主题"
                  onClick={() => setAppTheme(THEME.LIGHT)}
                >
                  {SunIcon}
                </button>
                <button
                  type="button"
                  className={clsx("zhirai-theme-toggle__btn", {
                    "is-active": themeIsDark,
                  })}
                  aria-pressed={themeIsDark}
                  aria-label="深色主题"
                  title="深色主题"
                  onClick={() => setAppTheme(THEME.DARK)}
                >
                  {MoonIcon}
                </button>
                <button
                  type="button"
                  className={clsx("zhirai-theme-toggle__btn", {
                    "is-active": themeIsSystem,
                  })}
                  aria-pressed={themeIsSystem}
                  aria-label="系统主题"
                  title="系统主题"
                  onClick={() => setAppTheme("system")}
                >
                  {DeviceDesktopIcon}
                </button>
              </div>
              <LanguageList style={{ width: 120 }} />

              {!isCollabDisabled && collabAPI && (
                <>
                  {collabError.message && (
                    <CollabError collabError={collabError} />
                  )}
                  <LiveCollaborationTrigger
                    isCollaborating={isCollaborating}
                    onSelect={() =>
                      setShareDialogState({ isOpen: true, type: "share" })
                    }
                    editorInterface={editorInterface}
                  />
                </>
              )}
            </div>
          );
        }}
        onLinkOpen={(element, event) => {
          if (element.link && isElementLink(element.link)) {
            event.preventDefault();
            excalidrawAPI?.scrollToContent(element.link, { animate: true });
          }
        }}
      >
        <AppMainMenu
          onCollabDialogOpen={onCollabDialogOpen}
          isCollaborating={isCollaborating}
          isCollabEnabled={!isCollabDisabled}
          theme={appTheme}
          setTheme={(theme) => setAppTheme(theme)}
          refresh={() => forceRefresh((prev) => !prev)}
          langCode={langCode}
          setLangCode={setLangCode}
        />
        <AppWelcomeScreen
          onCollabDialogOpen={onCollabDialogOpen}
          isCollabEnabled={!isCollabDisabled}
          excalidrawAPI={excalidrawAPI}
        />
        <OverwriteConfirmDialog>
          <OverwriteConfirmDialog.Actions.SaveToDisk />
        </OverwriteConfirmDialog>
        <AppFooter onChange={() => excalidrawAPI?.refresh()} />
        {isAiFeatureEnabled && excalidrawAPI && (
          <AIComponents excalidrawAPI={excalidrawAPI} />
        )}
        {isAiFeatureEnabled && <TTDDialogTrigger />}
        {isCollaborating && isOffline && (
          <div className="alertalert--warning">
            {t("alerts.collabOfflineWarning")}
          </div>
        )}
        {localStorageQuotaExceeded && (
          <div className="alert alert--danger">
            {t("alerts.localStorageQuotaExceeded")}
          </div>
        )}
        {excalidrawAPI && !isCollabDisabled && (
          <Collab excalidrawAPI={excalidrawAPI} />
        )}

        <AppSidebar />

        {errorMessage && (
          <ErrorDialog onClose={() => setErrorMessage("")}>
            {errorMessage}
          </ErrorDialog>
        )}

        {isCommandPaletteEnabled && (
          <CommandPalette
            customCommandPaletteItems={[
              ...(isCollabDisabled
                ? []
                : [
                    {
                      label: t("labels.liveCollaboration"),
                      category: DEFAULT_CATEGORIES.app,
                      keywords: [
                        "team",
                        "multiplayer",
                        "share",
                        "public",
                        "session",
                        "invite",
                      ],
                      icon: usersIcon,
                      perform: () => {
                        setShareDialogState({
                          isOpen: true,
                          type: "collaborationOnly",
                        });
                      },
                    },
                    {
                      label: t("roomDialog.button_stopSession"),
                      category: DEFAULT_CATEGORIES.app,
                      predicate: () => !!effectiveCollabAPI?.isCollaborating(),
                      keywords: [
                        "stop",
                        "session",
                        "end",
                        "leave",
                        "close",
                        "exit",
                        "collaboration",
                      ],
                      perform: () => {
                        if (effectiveCollabAPI) {
                          effectiveCollabAPI.stopCollaboration();
                          if (!effectiveCollabAPI.isCollaborating()) {
                            setShareDialogState({ isOpen: false });
                          }
                        }
                      },
                    },
                  ]),
              {
                ...CommandPalette.defaultItems.toggleTheme,
                perform: () => {
                  setAppTheme(
                    editorTheme === THEME.DARK ? THEME.LIGHT : THEME.DARK,
                  );
                },
              },
              {
                label: t("labels.installPWA"),
                category: DEFAULT_CATEGORIES.app,
                predicate: () => !!pwaEvent,
                perform: () => {
                  if (pwaEvent) {
                    pwaEvent.prompt();
                    pwaEvent.userChoice.then(() => {
                      // event cannot be reused, but we'll hopefully
                      // grab new one as the event should be fired again
                      pwaEvent = null;
                    });
                  }
                },
              },
            ]}
          />
        )}
        {isVisualDebuggerEnabled() && excalidrawAPI && (
          <DebugCanvas
            appState={excalidrawAPI.getAppState()}
            scale={window.devicePixelRatio}
            ref={debugCanvasRef}
          />
        )}
      </Excalidraw>
    </div>
  );
};

const ExcalidrawApp = () => {
  return (
    <TopErrorBoundary>
      <Provider store={appJotaiStore}>
        <ExcalidrawAPIProvider>
          <ExcalidrawWrapper />
        </ExcalidrawAPIProvider>
      </Provider>
    </TopErrorBoundary>
  );
};

export default ExcalidrawApp;
